import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from 'utils/services';

import { GetProgramProductsResponse, ProgramProductCard } from './programProducts.types';
import { normalizeProgramCards } from './programProducts.mappers';

/**
 * RTK Query API slice for the server-driven program-tab product cards
 * (`GET /pages/programs/:programId/products`).
 *
 * Returns one flat, ordered `items[]` of container cards — each holding 1..N medication rows, so a
 * standalone prescription is a one-row card and a shipping order's lines share a multi-row card. No groups.
 *
 * The endpoint may `404` until the backend deploys (LMD-649); the base query surfaces that as an `error`, so
 * the consuming section renders nothing rather than an error state. Keyed by `programId` so each program
 * page caches independently.
 */
export const programProductsApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    getProgramProducts: build.query<ProgramProductCard[], string>({
      // Server-composed card states change outside this client (an order ships, a renewal falls due) and
      // no mutation here can invalidate the tag, so never serve a cached feed on mount — refetch instead.
      // Without this, returning to the tab within 60s of firing a card CTA re-renders the pre-action card
      // and the action can be fired twice. Matches `homeProducts.ts`.
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, programId) => [{ id: programId, type: 'ProgramProducts' }],
      query: (programId) => ({ url: `/pages/programs/${programId}/products` }),
      transformResponse: (response: GetProgramProductsResponse) =>
        normalizeProgramCards(response?.data?.items)
    })
  }),
  reducerPath: 'programProductsApi',
  tagTypes: ['ProgramProducts']
});

export const { useGetProgramProductsQuery, useLazyGetProgramProductsQuery } = programProductsApi;
